import type { FormatNumberOptions } from 'react-intl';

import {
  FiatWalletAccountState,
  SCAEnrollmentState,
} from '__generated__/globalTypes';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useIntlContext } from 'contexts/intl';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useMonetaryAmount } from 'hooks/useMonetaryAmount';
import { fiatCurrencySymbols } from 'lib/fiat';
import { getMonetaryAmountIndex } from 'lib/monetaryAmount';

export const useFiatBalance = (formatOptions?: FormatNumberOptions) => {
  const {
    flags: { enableMangopaySca = false },
  } = useFeatureFlags();
  const {
    fiatWalletAccountable: accountable,
    fiatCurrency: fiatCurrencyFromUserSettings,
    currentUser,
  } = useCurrentUserContext();
  const { toMonetaryAmount } = useMonetaryAmount();

  const { formatNumber } = useIntlContext();

  const fiatCurrency =
    accountable?.publicInfo.currency || fiatCurrencyFromUserSettings;

  const availableBalanceMonetaryAmountParams = toMonetaryAmount(
    currentUser?.availableBalances?.[getMonetaryAmountIndex(fiatCurrency)]
  );

  const availableBalance =
    availableBalanceMonetaryAmountParams.toMajorUnit(fiatCurrency);
  const availableBalanceInCents =
    availableBalanceMonetaryAmountParams.readAmount(fiatCurrency);

  const availableBalanceWithCurrencySymbol = formatNumber(availableBalance, {
    style: 'currency',
    currency: fiatCurrency,
    ...formatOptions,
  });

  const needScaValidation =
    enableMangopaySca &&
    currentUser?.mangopayUser?.scaEnrollmentState ===
      SCAEnrollmentState.SCA_PENDING_CONFIRMATION;

  const isFiatWalletOwner =
    !!accountable &&
    [
      FiatWalletAccountState.OWNER,
      FiatWalletAccountState.VALIDATED_OWNER,
    ].includesValue(accountable.state);

  const canListAndTrade = isFiatWalletOwner && !needScaValidation;

  const canDepositAndWithdraw =
    !!accountable &&
    accountable.state === FiatWalletAccountState.VALIDATED_OWNER &&
    !needScaValidation;

  return {
    kycStatus: accountable?.kycStatus || undefined,
    refusedReason: accountable?.kycRefusedReason || undefined,
    fiatWalletState: accountable?.state,
    canPay: !!accountable,
    isFiatWalletOwner,
    canListAndTrade,
    canDepositAndWithdraw,
    fiatBalanceStatus: accountable?.state,
    availableBalance,
    availableBalanceInCents,
    fiatCurrency,
    fiatCurrencySymbol: fiatCurrencySymbols[fiatCurrency],
    availableBalanceWithCurrencySymbol,
    fiatWalletAccountable: accountable,
  };
};
